import { DestroyRef, Injectable, Injector, inject } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { NavigationEnd, NavigationStart, Router } from '@angular/router';
import { MsalBroadcastService, MsalService } from '@azure/msal-angular';
import { EventType, InteractionStatus } from '@azure/msal-browser';
import { ConfigService } from '@neworg-net/amsuite-core/config';
import { NotificationNavigationService } from '../notification-navigation.service';
import { NotifierRealtimeService } from '../notifier-realtime.service';
import { SignalRService } from '../signalr.service';
import { NotificationsStore } from './notifications.store';

/** One authenticated lifecycle for the global inbox and its two independent transports. */
@Injectable({ providedIn: 'root' })
export class NotificationSessionService {
  private readonly msal = inject(MsalService);
  private readonly broadcast = inject(MsalBroadcastService);
  private readonly router = inject(Router);
  private readonly config = inject(ConfigService);
  private readonly legacy = inject(SignalRService);
  private readonly notifier = inject(NotifierRealtimeService);
  private readonly store = inject(NotificationsStore);
  private readonly injector = inject(Injector);
  private readonly destroyRef = inject(DestroyRef);

  private initialized = false;
  private authReady = false;
  private suspended = false;
  private loginPending = false;
  private identity: string | null = null;
  private readonly onForeground = (): void => {
    if (document.hidden) return;
    this.reconcileIdentity();
    if (this.identity) this.store.refreshIfStale();
  };
  private readonly onStorage = (): void => this.reconcileIdentity();
  // Full reloads are also used for company changes: retain a pending destination.
  private readonly onPageHide = (): void => this.stopSession(false);
  private readonly onPageShow = (): void => this.reconcileIdentity();

  /** Registers listeners synchronously; no token or connection Promise blocks bootstrap. */
  initialize(): void {
    if (this.initialized) return;
    this.initialized = true;

    this.legacy.sub().pipe(takeUntilDestroyed(this.destroyRef)).subscribe(payload => {
      if (this.identity) this.store.handleLegacyEvent(payload);
    });
    this.legacy.starting$.pipe(takeUntilDestroyed(this.destroyRef)).subscribe(() => {
      if (this.identity) this.store.reconnected('legacy');
    });
    this.notifier.notificationReceived$.pipe(takeUntilDestroyed(this.destroyRef)).subscribe(ping => {
      if (this.identity) this.store.handleNotifierPing(ping);
    });
    this.notifier.connected$.pipe(takeUntilDestroyed(this.destroyRef)).subscribe(() => {
      if (this.identity) this.store.reconnected('notifier');
    });

    this.broadcast.inProgress$.pipe(takeUntilDestroyed(this.destroyRef)).subscribe(status => {
      this.authReady = status === InteractionStatus.None;
      if (this.authReady) this.reconcileIdentity();
    });
    this.broadcast.msalSubject$.pipe(takeUntilDestroyed(this.destroyRef)).subscribe(event => {
      if (event.eventType === EventType.LOGOUT_START) {
        this.stop();
      } else if (event.eventType === EventType.LOGIN_SUCCESS) {
        this.suspended = false;
        this.reconcileIdentity();
      } else if (event.eventType === EventType.ACTIVE_ACCOUNT_CHANGED) {
        // An impersonated business identity never changes the real MSAL account.
        this.reconcileIdentity();
      }
    });
    this.router.events.pipe(takeUntilDestroyed(this.destroyRef)).subscribe(event => {
      if (event instanceof NavigationStart && this.isLoginRoute(event.url)) {
        this.loginPending = true;
        this.stopSession(true);
      } else if (event instanceof NavigationEnd) {
        this.loginPending = this.isLoginRoute(event.urlAfterRedirects);
        this.reconcileIdentity();
      }
    });

    window.addEventListener('focus', this.onForeground);
    window.addEventListener('online', this.onForeground);
    document.addEventListener('visibilitychange', this.onForeground);
    window.addEventListener('storage', this.onStorage);
    window.addEventListener('pagehide', this.onPageHide);
    window.addEventListener('pageshow', this.onPageShow);
    this.destroyRef.onDestroy(() => {
      this.stopSession(false);
      window.removeEventListener('focus', this.onForeground);
      window.removeEventListener('online', this.onForeground);
      document.removeEventListener('visibilitychange', this.onForeground);
      window.removeEventListener('storage', this.onStorage);
      window.removeEventListener('pagehide', this.onPageHide);
      window.removeEventListener('pageshow', this.onPageShow);
    });
  }

  /** Called at the logout click, before the asynchronous legacy logout request. */
  stop(): void {
    this.suspended = true;
    this.stopSession(true);
  }

  private reconcileIdentity(): void {
    if (this.suspended) return;
    const account = this.msal.instance.getActiveAccount() ?? this.msal.instance.getAllAccounts()[0];
    const identity = account?.homeAccountId || account?.localAccountId || null;
    if (this.identity && identity !== this.identity) this.stopSession(true);
    if (!this.authReady) return;
    if (!identity || this.loginPending || this.isLoginRoute(this.router.url)) {
      if (this.identity) this.stopSession(true);
      return;
    }
    if (identity === this.identity) return;
    if (this.identity) this.stopSession(true);
    this.identity = identity;
    this.store.start();
    // A broken legacy transport must never prevent the new inbox from working.
    try { this.legacy.start(); } catch { /* REST recovery remains available. */ }
    try { this.notifier.start(); } catch { /* REST recovery remains available. */ }
  }

  private stopSession(clearPending: boolean): void {
    this.identity = null;
    this.store.stop();
    try { this.legacy.stop(); } catch { /* The other source still needs teardown. */ }
    try { this.notifier.stop(); } catch { /* Data was already cleared above. */ }
    if (clearPending) this.injector.get(NotificationNavigationService).clearPending();
  }

  private isLoginRoute(url: string): boolean {
    const path = url.split(/[?#]/u)[0];
    return path === this.config.loginRoute || path.startsWith(`${this.config.loginRoute}/`);
  }
}
